import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, Select } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { SelectOption } from 'contracts/Common';
import { useDocks, useValidation, useCompanies } from 'hooks';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { CreateDockReservedIntervalActions as CreateActions } from 'store/ducks/dockReservedIntervals';
import { TimeHelper } from 'utils';
import { CreateIntervalValidator } from 'validators/DockReservedIntervals';
import * as S from './styles';

interface Props {
  onCreate?: () => void;
}

interface GeneralState {
  companyId?: string | number;
}

export const IntervalCreationForm: React.FC<Props> = ({ onCreate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const [generalState, setGeneralState] = useState<GeneralState>({});
  const { handleFormErrors, handleApiErrors } = useValidation();
  const { companyOptions, loadingCompanies, fetchCompanies } =
    useCompanies();
  const { dockOptions, loadingDocks, fetchDocks } = useDocks();

  const { loading: creatingInterval, validationErrors } = useSelector(
    (state: RootState) => state.createDockReservedInterval
  );

  const onWarehouseChange = useCallback(
    (option: SelectOption | null): void => {
      formRef.current?.setFieldValue('dockId', null);
      setGeneralState((state) => ({
        ...state,
        companyId: option?.value,
      }));
      if (option) {
        fetchDocks({ companyId: option.value });
      }
    },
    [fetchDocks]
  );

  const onTimeBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value, name } = event.target;
      // manipulate the start time to be the next closest 5 minutes
      const newStartTime = TimeHelper.roundUpMinutes(value, 5);
      formRef.current?.setFieldValue(name, newStartTime);
    },
    []
  );

  const onSuccess = useCallback((): void => {
    onCreate && onCreate();
  }, [onCreate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        const { schema } = new CreateIntervalValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(CreateActions.request(validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess]
  );

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return (): void => {
      dispatch(CreateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Select
            name="companyId"
            label="Armazém"
            options={companyOptions}
            isLoading={loadingCompanies}
            onChange={onWarehouseChange}
          />
          <Select
            name="dockId"
            label="Doca"
            options={dockOptions}
            isLoading={loadingDocks}
            isDisabled={!generalState.companyId}
          />
        </S.FormRow>

        <S.FormRow>
          <Input name="startDate" label="Data de início" type="date" />
          <Input name="endDate" label="Data de término" type="date" />
          <Input
            name="startTime"
            label="Hora de início"
            type="time"
            onBlur={onTimeBlur}
          />
          <Input
            name="endTime"
            label="Hora de término"
            type="time"
            onBlur={onTimeBlur}
          />
        </S.FormRow>

        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/bloqueio-de-horarios">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {creatingInterval ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
